import React, { useState } from 'react';
import { HealthMetricsHistory } from '../../types/deviceTelemetry';

interface HealthMetricsChartProps {
  data: HealthMetricsHistory;
  deviceName: string;
}

const HealthMetricsChart: React.FC<HealthMetricsChartProps> = ({ data, deviceName }) => {
  const [selectedMetric, setSelectedMetric] = useState<string>('batteryLevel');

  // Available metrics with their display names and colors
  const metricConfig = {
    batteryLevel: { name: 'Battery Level', color: '#22c55e', unit: '%' },
    memoryUsage: { name: 'Memory Usage', color: '#3b82f6', unit: '%' },
    cpuUsage: { name: 'CPU Usage', color: '#f97316', unit: '%' },
    diskUsage: { name: 'Disk Usage', color: '#8b5cf6', unit: '%' },
    networkSignal: { name: 'Network Signal', color: '#06b6d4', unit: '%' },
    temperature: { name: 'Temperature', color: '#ef4444', unit: '°C' }
  };

  // Get available metrics from the data
  const availableMetrics = Object.keys(metricConfig).filter(metric => 
    data.metrics.some(point => point[metric as keyof typeof point] !== undefined)
  );

  // Get chart data for selected metric
  const getChartData = () => {
    return data.metrics
      .map(point => ({
        timestamp: point.timestamp,
        value: point[selectedMetric as keyof typeof point] as number
      }))
      .filter(point => point.value !== undefined && point.value !== null);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Simple SVG line chart implementation
  const renderChart = () => {
    const chartData = getChartData();
    if (chartData.length === 0) {
      return (
        <div className="chart-no-data">
          No data available for {metricConfig[selectedMetric as keyof typeof metricConfig]?.name}
        </div>
      );
    }

    const width = 600;
    const height = 300;
    const padding = 50;
    const chartWidth = width - 2 * padding;
    const chartHeight = height - 2 * padding;

    // Find min/max values
    const values = chartData.map(point => point.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue || 1;

    // Create SVG path for the line
    const pathData = chartData.map((point, index) => {
      const x = padding + (index / (chartData.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((point.value - minValue) / valueRange) * chartHeight;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    // Create area path for fill
    const areaPath = pathData + ` L ${padding + chartWidth} ${padding + chartHeight} L ${padding} ${padding + chartHeight} Z`;

    const config = metricConfig[selectedMetric as keyof typeof metricConfig];

    return (
      <div className="chart-container">
        <svg width={width} height={height} className="metrics-chart">
          {/* Grid lines */}
          <defs>
            <linearGradient id={`gradient-${selectedMetric}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: config.color, stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: config.color, stopOpacity: 0.1 }} />
            </linearGradient>
          </defs>
          
          {/* Horizontal grid lines */}
          {[0, 25, 50, 75, 100].map(percent => {
            const y = padding + chartHeight - (percent / 100) * chartHeight;
            return (
              <g key={percent}>
                <line
                  x1={padding}
                  y1={y}
                  x2={padding + chartWidth}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  fontSize="12"
                  fill="#6b7280"
                  textAnchor="end"
                >
                  {selectedMetric === 'temperature' ? 
                    Math.round(minValue + (percent / 100) * valueRange) :
                    percent
                  }{config.unit}
                </text>
              </g>
            );
          })}

          {/* Vertical grid lines and time labels */}
          {chartData.map((point, index) => {
            if (index % Math.ceil(chartData.length / 6) === 0) {
              const x = padding + (index / (chartData.length - 1)) * chartWidth;
              return (
                <g key={index}>
                  <line
                    x1={x}
                    y1={padding}
                    x2={x}
                    y2={padding + chartHeight}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                  />
                  <text
                    x={x}
                    y={padding + chartHeight + 20}
                    fontSize="12"
                    fill="#6b7280"
                    textAnchor="middle"
                  >
                    {formatTimestamp(point.timestamp)}
                  </text>
                </g>
              );
            }
            return null;
          })}

          {/* Area fill */}
          <path
            d={areaPath}
            fill={`url(#gradient-${selectedMetric})`}
          />

          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke={config.color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {chartData.map((point, index) => {
            const x = padding + (index / (chartData.length - 1)) * chartWidth;
            const y = padding + chartHeight - ((point.value - minValue) / valueRange) * chartHeight;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="4"
                fill={config.color}
                stroke="white"
                strokeWidth="2"
              >
                <title>
                  {formatTimestamp(point.timestamp)}: {point.value}{config.unit}
                </title>
              </circle>
            );
          })}
        </svg>

        {/* Chart legend */}
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: config.color }}></div>
            <span>{config.name}</span>
          </div>
          <div className="legend-stats">
            <span>Min: {Math.min(...values).toFixed(1)}{config.unit}</span>
            <span>Avg: {(values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)}{config.unit}</span>
            <span>Max: {Math.max(...values).toFixed(1)}{config.unit}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="health-metrics-chart">
      {/* Metric selector */}
      <div className="metric-selector">
        <div className="metric-tabs">
          {availableMetrics.map(metric => {
            const config = metricConfig[metric as keyof typeof metricConfig];
            return (
              <button
                key={metric}
                onClick={() => setSelectedMetric(metric)}
                className={`metric-tab ${selectedMetric === metric ? 'active' : ''}`}
                style={{
                  borderBottomColor: selectedMetric === metric ? config.color : 'transparent'
                }}
              >
                <span className="metric-name">{config.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="chart-area">
        {renderChart()}
      </div>

      {/* Export button */}
      <div className="chart-actions">
        <button
          onClick={() => {
            const csvContent = [
              ['Timestamp', metricConfig[selectedMetric as keyof typeof metricConfig]?.name],
              ...getChartData().map(point => [point.timestamp, point.value.toString()])
            ].map(row => row.join(',')).join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${deviceName}-${selectedMetric}-${data.period}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="export-button"
        >
          Export Data
        </button>
      </div>
    </div>
  );
};

export default HealthMetricsChart;